import { Skeleton } from "@/components/atoms";
import { useUIColors } from "@/core/hooks";
import React from "react";
import { StyleSheet, View } from "react-native";

export interface ListingCardSkeletonProps {
  /** Same as ListingCard.fullWidth — fill parent column width. */
  readonly fullWidth?: boolean;
  /** Same as ListingCard.singleColumn — taller media band. */
  readonly singleColumn?: boolean;
}

const SKELETON_MEDIA_HEIGHT = 200;
const SKELETON_MEDIA_HEIGHT_SINGLE = 260;

/** Mirrors ListingCard's box model so the grid doesn't shift when data arrives. */
export function ListingCardSkeleton({
  fullWidth = false,
  singleColumn = false,
}: Readonly<ListingCardSkeletonProps>) {
  const c = useUIColors();
  const mediaHeight = singleColumn
    ? SKELETON_MEDIA_HEIGHT_SINGLE
    : SKELETON_MEDIA_HEIGHT;

  return (
    <View
      style={[
        styles.container,
        fullWidth ? styles.containerFullWidth : styles.containerInGrid,
        {
          backgroundColor: "#FFFFFF",
          borderColor: c.outlineInactive,
        },
      ]}
    >
      <View style={[styles.imageWrap, { height: mediaHeight }]}>
        <Skeleton width="100%" height={mediaHeight} borderRadius={8} />
      </View>

      <View style={styles.content}>
        <Skeleton width="60%" height={25} borderRadius={4} />

        <View style={styles.specsRow}>
          <View style={styles.specItem}>
            <Skeleton width="80%" height={18} borderRadius={4} />
          </View>
          <View style={styles.specItem}>
            <Skeleton width="80%" height={18} borderRadius={4} />
          </View>
          <View style={styles.specItem}>
            <Skeleton width="80%" height={18} borderRadius={4} />
          </View>
        </View>
      </View>

      <View
        style={[
          styles.footer,
          {
            backgroundColor: c.backgroundLevel2,
            flexDirection: "row",
          },
        ]}
      >
        <Skeleton width={120} height={22} borderRadius={4} />
        <Skeleton width={100} height={18} borderRadius={4} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 0,
    padding: 8,
    width: "100%",
    minWidth: 0,
    flexDirection: "column",
    gap: 8,
  },
  containerInGrid: {
    flex: 1,
    maxWidth: 443,
  },
  containerFullWidth: {
    maxWidth: "100%",
    width: "100%",
    alignSelf: "stretch",
  },
  imageWrap: {
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
  },
  content: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
    flex: 1,
    gap: 12,
    justifyContent: "flex-start",
  },
  specsRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  specItem: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    paddingRight: 8,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 8,
    marginHorizontal: 12,
    marginBottom: 0,
    flexWrap: "wrap",
    gap: 8,
  },
});
